// src/modules/agentic/PlanningLab.jsx
// Planning Lab — CoT · ToT · MCTS · task decomposition · plan-execute-reflect

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#fbbf24';
const P  = ['#fbbf24','#22d3ee','#4ade80','#a78bfa','#f87171','#fb923c'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── CoT STEPS ────────────────────────────────────────────────────────────────
const COT_EXAMPLES = {
  math: {
    task: 'If a train travels 120km in 1.5 hours, what is its speed? How long to travel 200km?',
    steps: [
      {n:1, thought:'To find speed: speed = distance / time', calc:'120 ÷ 1.5 = 80 km/h'},
      {n:2, thought:'Now find time for 200km: time = distance / speed', calc:'200 ÷ 80 = 2.5 hours'},
      {n:3, thought:'Convert 2.5 hours to hours and minutes', calc:'2h 30min'},
      {n:4, thought:'Verify: 80 × 2.5 = 200 ✓', calc:'Answer: 80 km/h, 2h 30min'},
    ]
  },
  logic: {
    task: 'All dogs are mammals. All mammals breathe air. Does a dog breathe air?',
    steps: [
      {n:1, thought:'Identify premises', calc:'P1: dog → mammal, P2: mammal → breathes_air'},
      {n:2, thought:'Apply transitivity', calc:'dog → mammal → breathes_air'},
      {n:3, thought:'Check target: does dog satisfy breathes_air?', calc:'Yes, by transitive chain'},
      {n:4, thought:'Conclusion is valid', calc:'Answer: Yes, a dog breathes air.'},
    ]
  },
  code: {
    task: 'Write a function to find the longest common subsequence of two strings.',
    steps: [
      {n:1, thought:'Recognize as dynamic programming problem', calc:'LCS(s1, s2) — classic DP'},
      {n:2, thought:'Define subproblem: dp[i][j] = LCS of s1[:i] and s2[:j]', calc:'Build n×m table'},
      {n:3, thought:'Recurrence: if s1[i]==s2[j]: dp[i][j]=dp[i-1][j-1]+1 else max(dp[i-1][j],dp[i][j-1])', calc:'Fill table bottom-up'},
      {n:4, thought:'Read result from dp[n][m], backtrace to find actual sequence', calc:'Return lcs_string'},
    ]
  }
};

// ─── TREE OF THOUGHT SVG ─────────────────────────────────────────────────────
function ToTTree({ task, breadth, depth, scores, selected }) {
  const W=700, H=320, pad=20;
  // Build tree layout
  const nodes = [];
  const edges = [];

  // Root
  nodes.push({id:'root', x:W/2, y:pad+20, label:task.slice(0,24)+'…', score:null, selected:true, depth:0});

  // Generate tree
  let nodeId = 0;
  const genChildren = (parentId, parentX, parentY, d) => {
    if(d >= depth) return;
    const n = Math.min(breadth, 4);
    const spread = (W-pad*4) / Math.pow(breadth, d);
    for(let i=0; i<n; i++){
      const id = `n${nodeId++}`;
      const x = parentX - spread*(n-1)/2 + spread*i;
      const y = parentY + 70;
      const score = scores[`${d}-${i}`] || +(0.3+Math.random()*0.65).toFixed(2);
      const isBest = i === Math.floor(n/2) && d < depth-1;
      nodes.push({id, x, y, label:`Option ${i+1}`, score, selected:isBest||d===depth-1&&i===1, depth:d+1});
      edges.push({from:nodes.find(n=>n.id===parentId), to:{x,y}});
      if(d+1<depth) genChildren(id, x, y, d+1);
    }
  };

  genChildren('root', W/2, pad+20, 0);

  const maxScore = Math.max(...nodes.filter(n=>n.score).map(n=>n.score));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {edges.map((e,i) => e.from && (
        <line key={i} x1={e.from.x} y1={e.from.y+16} x2={e.to.x} y2={e.to.y-16}
          stroke="#1e293b" strokeWidth={1.5}/>
      ))}
      {nodes.map(n => {
        const isTop = n.score === maxScore;
        const color = n.depth===0 ? '#22d3ee' : isTop ? '#4ade80' : n.score > 0.65 ? '#fbbf24' : '#64748b';
        return (
          <g key={n.id}>
            <rect x={n.x-36} y={n.y-16} width={72} height={32} rx={4}
              fill={isTop?'#4ade8015':n.depth===0?'#22d3ee15':'#0f172a'}
              stroke={color} strokeWidth={isTop?2:1}/>
            <text x={n.x} y={n.y-4} textAnchor="middle" style={{fontSize:'8px',fill:color,fontFamily:'JetBrains Mono',fontWeight:isTop?700:400}}>{n.label}</text>
            {n.score&&<text x={n.x} y={n.y+8} textAnchor="middle" style={{fontSize:'8px',fill:color,fontFamily:'JetBrains Mono',fontWeight:700}}>s={n.score}</text>}
          </g>
        );
      })}
      <text x={W/2} y={H-6} textAnchor="middle" style={{fontSize:'8px',fill:'#475569',fontFamily:'JetBrains Mono'}}>
        {nodes.length} nodes explored · best path in green
      </text>
    </svg>
  );
}

// ─── MCTS SVG ─────────────────────────────────────────────────────────────────
function MCTSSvg({ iteration }) {
  const W=700, H=200;
  const phases = ['SELECT','EXPAND','SIMULATE','BACKPROPAGATE'];
  const current = iteration % 4;
  const colors = ['#22d3ee','#4ade80','#fbbf24','#a78bfa'];
  const descs = [
    'Traverse tree using UCB1 score\nPick node with best exploit/explore balance',
    'Add new child node\nto the selected node',
    'Run random rollout\nfrom new node to estimate value',
    'Update win/visit counts\nup the path to root',
  ];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {phases.map((p,i)=>{
        const x=20+i*(W-40)/4+((W-40)/4-130)/2, isActive=current===i;
        const color=colors[i];
        return (
          <g key={p}>
            <rect x={x} y={20} width={130} height={130} rx={5}
              fill={isActive?color+'22':'#0f172a'} stroke={color} strokeWidth={isActive?2.5:1}/>
            <text x={x+65} y={48} textAnchor="middle" style={{fontSize:'10px',fill:color,fontFamily:'JetBrains Mono',fontWeight:700}}>{p}</text>
            <text x={x+65} y={64} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{i+1} of 4</text>
            {descs[i].split('\n').map((l,j)=>(
              <text key={j} x={x+65} y={90+j*16} textAnchor="middle" style={{fontSize:'8px',fill:isActive?'var(--t2)':'#475569',fontFamily:'JetBrains Mono'}}>{l}</text>
            ))}
            {isActive&&<rect x={x+2} y={22} width={126} height={4} rx={2} fill={color} opacity={0.6}/>}
          </g>
        );
      })}
      {/* Arrows between phases */}
      {[0,1,2].map(i=>{
        const x1=20+(i+1)*(W-40)/4, x2=x1, mid=15;
        return <path key={i} d={`M${x1-20},80 L${x1+20},80`} fill="none" stroke="#334155" strokeWidth={2} markerEnd="url(#ar)"/>;
      })}
      <text x={W/2} y={H-10} textAnchor="middle" style={{fontSize:'9px',fill:colors[current],fontFamily:'JetBrains Mono',fontWeight:700}}>
        Iteration {iteration+1} — current phase: {phases[current]}
      </text>
      <defs>
        <marker id="ar" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#334155"/>
        </marker>
      </defs>
    </svg>
  );
}

// ─── TASK DECOMPOSITION ───────────────────────────────────────────────────────
const DECOMP_TASKS = {
  research: {
    goal: 'Write a 5-page research report on quantum computing',
    subtasks: [
      {id:1, title:'Define scope & outline', deps:[], est:'10min', tool:'llm', status:'done'},
      {id:2, title:'Search recent papers (2022-2024)', deps:[1], est:'5min', tool:'search', status:'done'},
      {id:3, title:'Search industry applications', deps:[1], est:'5min', tool:'search', status:'running'},
      {id:4, title:'Extract key concepts from papers', deps:[2], est:'8min', tool:'llm', status:'pending'},
      {id:5, title:'Write introduction section', deps:[1,4], est:'12min', tool:'llm', status:'pending'},
      {id:6, title:'Write technical section', deps:[4], est:'15min', tool:'llm', status:'pending'},
      {id:7, title:'Write applications section', deps:[3,4], est:'12min', tool:'llm', status:'pending'},
      {id:8, title:'Write conclusion', deps:[5,6,7], est:'8min', tool:'llm', status:'pending'},
      {id:9, title:'Compile & format final doc', deps:[5,6,7,8], est:'5min', tool:'write_file', status:'pending'},
    ]
  },
  software: {
    goal: 'Build and deploy a REST API for user authentication',
    subtasks: [
      {id:1, title:'Design API schema & endpoints', deps:[], est:'15min', tool:'llm', status:'done'},
      {id:2, title:'Set up project structure', deps:[1], est:'5min', tool:'run_code', status:'done'},
      {id:3, title:'Implement /register endpoint', deps:[2], est:'20min', tool:'run_code', status:'running'},
      {id:4, title:'Implement /login + JWT', deps:[2], est:'20min', tool:'run_code', status:'pending'},
      {id:5, title:'Implement /refresh token', deps:[4], est:'10min', tool:'run_code', status:'pending'},
      {id:6, title:'Write unit tests', deps:[3,4,5], est:'15min', tool:'run_code', status:'pending'},
      {id:7, title:'Run tests & fix bugs', deps:[6], est:'10min', tool:'run_code', status:'pending'},
      {id:8, title:'Write deployment config', deps:[7], est:'8min', tool:'write_file', status:'pending'},
      {id:9, title:'Deploy to server', deps:[8], est:'10min', tool:'run_code', status:'pending'},
    ]
  }
};

const statusColor = {done:'#4ade80', running:'#fbbf24', pending:'#334155'};
const statusIcon  = {done:'✓', running:'⟳', pending:'○'};

// ─── REFLECT LOOP ─────────────────────────────────────────────────────────────
const REFLECT_STEPS = [
  {phase:'PLAN',    color:'#22d3ee',  content:'Goal: "Build a web scraper for news articles"\n1. Search for news sites with RSS feeds\n2. Write Python scraper\n3. Test on 5 sites\n4. Store results to DB'},
  {phase:'EXECUTE', color:'#4ade80',  content:'Running step 1: found 8 RSS feeds ✓\nRunning step 2: wrote scraper… \nERROR: 3/5 sites block bots (403 Forbidden)'},
  {phase:'OBSERVE', color:'#fbbf24',  content:'2 of 5 sites succeeded\n3 sites returned 403 errors\nBot detection active on major news sites'},
  {phase:'REFLECT', color:'#a78bfa',  content:'Original plan assumed unrestricted access.\nMajor assumption violated. Need to revise approach:\n→ Use official APIs instead of scraping\n→ Or add user-agent rotation + delays'},
  {phase:'REVISE',  color:'#fb923c',  content:'NEW PLAN:\n1. Check for official news APIs (NewsAPI, Guardian)\n2. Register for API key\n3. Fetch via API — no bot detection\n4. Fall back to RSS-only for remaining sites'},
];

const TABS = ['cot','tot','mcts','decomposition','reflect'];

export default function PlanningLab() {
  const [tab, setTab]     = useState('cot');
  const [logs, setLogs]   = useState([{ts:'00:00:00',msg:'Planning Lab — how agents reason, plan, and adapt their strategies.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  // CoT state
  const [cotTask, setCotTask] = useState('math');
  const [cotStep, setCotStep] = useState(0);
  const [cotRunning, setCotRunning] = useState(false);
  const cotRef = useRef();

  // ToT state
  const [totBreadth, setTotBreadth] = useState(3);
  const [totDepth,   setTotDepth]   = useState(3);
  const [totScores,  setTotScores]  = useState({});
  const [totTask,    setTotTask]    = useState('Plan a product launch strategy for a new mobile app');

  // MCTS state
  const [mctsIter,  setMctsIter]   = useState(0);
  const [mctsRun,   setMctsRun]    = useState(false);
  const mctsRef = useRef();

  // Decomp state
  const [decompTask, setDecompTask] = useState('research');

  // Reflect state
  const [reflectStep, setReflectStep] = useState(0);
  const [reflectRun,  setReflectRun]  = useState(false);
  const reflRef = useRef();

  const runCoT = () => {
    setCotStep(0); setCotRunning(true);
    const steps = COT_EXAMPLES[cotTask].steps;
    let i=0;
    cotRef.current = setInterval(()=>{
      i++; setCotStep(i);
      if(i>=steps.length){ clearInterval(cotRef.current); setCotRunning(false); log(`CoT complete: ${i} reasoning steps`,'ok'); }
    }, 900);
  };

  const toggleMCTS = () => {
    if(mctsRun){ clearInterval(mctsRef.current); setMctsRun(false); return; }
    setMctsRun(true);
    mctsRef.current = setInterval(()=>setMctsIter(s=>s+1), 800);
  };

  const runReflect = () => {
    setReflectStep(0); setReflectRun(true);
    let i=0;
    reflRef.current = setInterval(()=>{
      i++; setReflectStep(i);
      if(i>=REFLECT_STEPS.length){ clearInterval(reflRef.current); setReflectRun(false); log('Plan→Execute→Reflect→Revise cycle complete','ok'); }
    }, 1000);
  };

  const genToTScores = () => {
    const s = {};
    for(let d=0;d<totDepth;d++) for(let i=0;i<totBreadth;i++) s[`${d}-${i}`] = +(0.3+Math.random()*0.65).toFixed(2);
    setTotScores(s);
    log(`ToT: breadth=${totBreadth} depth=${totDepth} — ${totBreadth**totDepth} leaves explored`,'ok');
  };

  useEffect(()=>()=>{clearInterval(cotRef.current);clearInterval(mctsRef.current);clearInterval(reflRef.current);},[]);

  const dtask = DECOMP_TASKS[decompTask];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── CoT ── */}
      {tab==='cot'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="TASK" accent="#22d3ee">
              {Object.entries(COT_EXAMPLES).map(([k,v])=>(
                <button key={k} onClick={()=>{setCotTask(k);setCotStep(0);clearInterval(cotRef.current);setCotRunning(false);}} style={{display:'block',width:'100%',padding:'8px',background:cotTask===k?'#22d3ee18':'var(--deep)',border:`1px solid ${cotTask===k?'#22d3ee':'var(--b0)'}`,color:cotTask===k?'#22d3ee':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',marginBottom:'4px',fontFamily:'JetBrains Mono',fontWeight:cotTask===k?700:400,textAlign:'left'}}>
                  {k.toUpperCase()}<br/><span style={{fontWeight:400,color:'var(--t3)',fontSize:'8px'}}>{v.task.slice(0,44)}…</span>
                </button>
              ))}
              <Btn onClick={runCoT} disabled={cotRunning} color="#22d3ee" style={{width:'100%',marginTop:'4px'}}>{cotRunning?'THINKING…':'RUN CoT →'}</Btn>
            </Panel>
            <Panel title="WHAT IS CoT?" accent={AC}>
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                <div style={{color:AC,fontWeight:700,marginBottom:'4px'}}>Chain-of-Thought</div>
                Prompt the model to reason step-by-step before answering. Dramatically improves accuracy on multi-step problems.
                <div style={{marginTop:'6px',padding:'6px',background:'var(--deep)',borderRadius:'2px',fontFamily:'JetBrains Mono',fontSize:'8px',color:'#a78bfa'}}>
                  "Let's think step by step…"
                </div>
                <div style={{marginTop:'6px'}}>+34% on GSM8K (math), +18% on StrategyQA vs direct answer.</div>
              </div>
            </Panel>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title={`CHAIN-OF-THOUGHT — "${COT_EXAMPLES[cotTask].task.slice(0,55)}…"`} accent="#22d3ee">
              <div style={{display:'flex',flexDirection:'column',gap:'5px',marginBottom:'10px'}}>
                {COT_EXAMPLES[cotTask].steps.slice(0,cotStep).map((s,i)=>(
                  <div key={i} style={{display:'flex',gap:'10px',padding:'10px 12px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${P[i%P.length]}`,animation:'fadeIn .4s'}}>
                    <div style={{minWidth:22,height:22,borderRadius:'50%',background:P[i%P.length]+'33',border:`1px solid ${P[i%P.length]}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'9px',color:P[i%P.length],fontWeight:700}}>{s.n}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'3px'}}>{s.thought}</div>
                      <div style={{fontSize:'9px',color:P[i%P.length],fontFamily:'JetBrains Mono'}}>{s.calc}</div>
                    </div>
                  </div>
                ))}
                {cotStep===0&&<div style={{height:120,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Select task and run CoT</div>}
              </div>
              {cotStep>0&&<div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                <Metric label="Steps shown" value={cotStep} color="#22d3ee"/>
                <Metric label="Total steps" value={COT_EXAMPLES[cotTask].steps.length} color={AC}/>
                <Metric label="Complete" value={cotStep>=COT_EXAMPLES[cotTask].steps.length?'✓ YES':'…'} color="#4ade80"/>
              </div>}
            </Panel>
            <Panel title="CoT vs DIRECT vs ZERO-SHOT CoT" accent={AC}>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={[
                  {task:'Math', direct:45, cot:80, zero_shot_cot:73},
                  {task:'Logic', direct:52, cot:84, zero_shot_cot:79},
                  {task:'Coding', direct:61, cot:88, zero_shot_cot:82},
                  {task:'Science', direct:58, cot:79, zero_shot_cot:74},
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="task" tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,100]} label={{value:'accuracy %',fill:'var(--t3)',fontSize:8,angle:-90,position:'insideLeft'}}/>
                  <Tooltip/><Legend/>
                  <Bar dataKey="direct"       fill="#64748b"  name="Direct" radius={[2,2,0,0]}/>
                  <Bar dataKey="zero_shot_cot" fill="#22d3ee" name="Zero-shot CoT" radius={[2,2,0,0]}/>
                  <Bar dataKey="cot"          fill="#fbbf24"  name="Few-shot CoT" radius={[2,2,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        </div>
      )}

      {/* ── ToT ── */}
      {tab==='tot'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:'12px'}}>
            <Panel title="TREE CONFIG" accent="#a78bfa">
              <input value={totTask} onChange={e=>setTotTask(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'10px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'10px',boxSizing:'border-box'}}/>
              <Sl label="Breadth (branches)" value={totBreadth} min={2} max={4} step={1} onChange={setTotBreadth} color="#a78bfa"/>
              <Sl label="Depth (layers)" value={totDepth} min={1} max={4} step={1} onChange={setTotDepth} color="#fbbf24"/>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px'}}>Nodes to explore: <span style={{color:'#f87171',fontWeight:700}}>{totBreadth**totDepth}</span> leaves</div>
              <Btn onClick={genToTScores} color="#a78bfa" style={{width:'100%'}}>EXPLORE TREE →</Btn>
            </Panel>
            <Panel title="TREE OF THOUGHT — EXPLORING MULTIPLE REASONING PATHS" accent="#a78bfa">
              <ToTTree task={totTask} breadth={totBreadth} depth={totDepth} scores={totScores} selected={[]}/>
              <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'8px',lineHeight:1.7}}>
                Each node = one reasoning step. Score = estimated progress toward goal. LLM evaluates each node and prunes low-score branches. Best path selected via beam search or BFS.
              </div>
            </Panel>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="CoT vs ToT" accent="#fbbf24">
              {[
                {aspect:'Structure',cot:'Linear chain',tot:'Branching tree'},
                {aspect:'Backtracking',cot:'None — committed',tot:'Yes — prune bad branches'},
                {aspect:'LLM calls',cot:'1 pass',tot:'O(b×d) — expensive'},
                {aspect:'Best for',cot:'Well-defined sequential tasks',tot:'Open-ended creative/planning tasks'},
                {aspect:'Token cost',cot:'Low',tot:'High (scales with tree size)'},
              ].map(r=>(
                <div key={r.aspect} style={{display:'grid',gridTemplateColumns:'80px 1fr 1fr',gap:'6px',padding:'5px',borderBottom:'1px solid var(--b0)',fontSize:'9px'}}>
                  <span style={{color:'var(--t3)',fontWeight:700}}>{r.aspect}</span>
                  <span style={{color:'#22d3ee'}}>{r.cot}</span>
                  <span style={{color:'#a78bfa'}}>{r.tot}</span>
                </div>
              ))}
            </Panel>
            <Panel title="WHEN TO USE EACH" accent="#4ade80">
              {[
                {strat:'Direct answer',use:'Factual recall, classification',color:'#64748b'},
                {strat:'Chain-of-Thought',use:'Math, logic, step-by-step coding',color:'#22d3ee'},
                {strat:'Zero-shot CoT',use:'Quick reasoning boost, no examples',color:'#4ade80'},
                {strat:'Tree-of-Thought',use:'Creative writing, planning, puzzles',color:'#a78bfa'},
                {strat:'MCTS',use:'Game-like decisions, iterative search',color:'#fbbf24'},
                {strat:'RAG + CoT',use:'Knowledge-heavy + reasoning tasks',color:'#f87171'},
              ].map(({strat,use,color})=>(
                <div key={strat} style={{display:'flex',gap:'8px',marginBottom:'5px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${color}`}}>
                  <span style={{fontSize:'9px',color,fontWeight:700,minWidth:110}}>{strat}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{use}</span>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {/* ── MCTS ── */}
      {tab==='mcts'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="MONTE CARLO TREE SEARCH — 4-PHASE LOOP" accent="#4ade80">
            <MCTSSvg iteration={mctsIter}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
              <Btn onClick={toggleMCTS} color={mctsRun?'#f87171':'#4ade80'} style={{flex:1}}>{mctsRun?'STOP':'▶ ANIMATE MCTS'}</Btn>
              <Btn onClick={()=>{clearInterval(mctsRef.current);setMctsRun(false);setMctsIter(0);}} color="#64748b">RESET</Btn>
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            {[
              {phase:'SELECT',color:'#22d3ee',formula:'UCB1 = V_i + C·√(ln(N)/n_i)',desc:'Balance exploitation (V_i) vs exploration (C term). Pick best leaf to expand next.'},
              {phase:'EXPAND',color:'#4ade80',formula:'add child node to selected',desc:'Create one or more new nodes representing new states/decisions from the selected node.'},
              {phase:'SIMULATE',color:'#fbbf24',formula:'rollout → reward r ∈ [0,1]',desc:'Random playout from new node to terminal state. Estimate value without full tree expansion.'},
              {phase:'BACKPROP',color:'#a78bfa',formula:'V = (V·n + r) / (n+1)',desc:'Update win counts and visit counts up the path to root. Influences future selections.'},
            ].map(p=>(
              <Panel key={p.phase} title={p.phase} accent={p.color}>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'9px',color:p.color,padding:'5px',background:'var(--deep)',borderRadius:'2px',marginBottom:'6px'}}>{p.formula}</div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{p.desc}</div>
              </Panel>
            ))}
          </div>
          <Panel title="MCTS IN LLM AGENTS" accent={AC}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {use:'Code generation',detail:'AlphaCode-style: generate candidates, test, score, backprop correctness signal',color:'#4ade80'},
                {use:'Dialog planning',detail:'Each node = possible agent reply. Simulate user reactions. Pick best reply tree.',color:'#22d3ee'},
                {use:'Multi-step reasoning',detail:'RAP (Reasoning via Planning): MCTS over thought steps, value = self-evaluation.',color:'#a78bfa'},
              ].map(({use,detail,color})=>(
                <div key={use} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'5px'}}>{use}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{detail}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── DECOMPOSITION ── */}
      {tab==='decomposition'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'flex',gap:'8px',marginBottom:'4px'}}>
            {Object.keys(DECOMP_TASKS).map(k=>(
              <button key={k} onClick={()=>setDecompTask(k)} style={{padding:'7px 16px',background:decompTask===k?AC+'18':'var(--deep)',border:`1px solid ${decompTask===k?AC:'var(--b0)'}`,color:decompTask===k?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:decompTask===k?700:400}}>{k.toUpperCase()}</button>
            ))}
          </div>
          <Panel title={`TASK DECOMPOSITION — "${dtask.goal}"`} accent={AC}>
            <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
              {dtask.subtasks.map(t=>(
                <div key={t.id} style={{display:'grid',gridTemplateColumns:'24px 1fr 60px 80px 70px',gap:'8px',padding:'8px 10px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${statusColor[t.status]}`,alignItems:'center'}}>
                  <span style={{fontSize:'10px',color:statusColor[t.status],fontFamily:'JetBrains Mono',fontWeight:700}}>{statusIcon[t.status]}</span>
                  <span style={{fontSize:'10px',color:t.status==='pending'?'var(--t3)':'var(--t1)'}}>{t.title}</span>
                  <span style={{fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>{t.est}</span>
                  <span style={{fontSize:'8px',color:'#64748b',fontFamily:'JetBrains Mono'}}>{t.deps.length?`deps: [${t.deps.join(',')}]`:'no deps'}</span>
                  <span style={{fontSize:'8px',padding:'2px 6px',background:'#ffffff10',borderRadius:'2px',color:'var(--t3)',fontFamily:'JetBrains Mono',textAlign:'center'}}>{t.tool}</span>
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginTop:'10px'}}>
              <Metric label="Total tasks" value={dtask.subtasks.length} color={AC}/>
              <Metric label="Done" value={dtask.subtasks.filter(t=>t.status==='done').length} color="#4ade80"/>
              <Metric label="Running" value={dtask.subtasks.filter(t=>t.status==='running').length} color="#fbbf24"/>
              <Metric label="Pending" value={dtask.subtasks.filter(t=>t.status==='pending').length} color="#64748b"/>
            </div>
          </Panel>
          <Panel title="DECOMPOSITION STRATEGIES" accent="#22d3ee">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {[
                {name:'Sequential',desc:'A → B → C → D. Simple, but slow. Each step waits for previous.',good:'Tasks with strong dependencies',bad:'Long wall-clock time',color:'#22d3ee'},
                {name:'Parallel',desc:'A, B, C run simultaneously. Merge results at the end.',good:'Independent sub-tasks',bad:'Harder to coordinate, race conditions',color:'#4ade80'},
                {name:'Hierarchical',desc:'Top task → mid-level tasks → atomic actions. Recursive decomposition.',good:'Complex long-horizon tasks',bad:'Deep nesting = hard to debug',color:'#a78bfa'},
              ].map(({name,desc,good,bad,color})=>(
                <div key={name} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'5px'}}>{name}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',lineHeight:1.6}}>{desc}</div>
                  <div style={{fontSize:'8px',color:'#4ade80',marginBottom:'2px'}}>✓ {good}</div>
                  <div style={{fontSize:'8px',color:'#f87171'}}>✗ {bad}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── REFLECT ── */}
      {tab==='reflect'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="PLAN → EXECUTE → OBSERVE → REFLECT → REVISE" accent="#a78bfa">
            <div style={{display:'flex',flexDirection:'column',gap:'5px',marginBottom:'10px'}}>
              {REFLECT_STEPS.slice(0, reflectStep||0).map((s,i)=>(
                <div key={i} style={{display:'flex',gap:'12px',padding:'10px 14px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${s.color}`,animation:'fadeIn .4s'}}>
                  <div style={{minWidth:70}}>
                    <span style={{fontSize:'9px',color:s.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{s.phase}</span>
                  </div>
                  <pre style={{margin:0,fontSize:'9px',color:'var(--t2)',fontFamily:'JetBrains Mono',lineHeight:1.6,whiteSpace:'pre-wrap'}}>{s.content}</pre>
                </div>
              ))}
              {!reflectStep&&<div style={{height:120,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run the reflect loop</div>}
            </div>
            <div style={{display:'flex',gap:'8px'}}>
              <Btn onClick={runReflect} disabled={reflectRun} color="#a78bfa" style={{flex:1}}>{reflectRun?'REFLECTING…':'▶ RUN REFLECT LOOP'}</Btn>
              <Btn onClick={()=>{clearInterval(reflRef.current);setReflectRun(false);setReflectStep(0);}} color="#64748b">RESET</Btn>
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="REFLEXION (Shinn et al. 2023)" accent="#4ade80">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                Reflexion = verbal reinforcement learning. Instead of gradient updates, the agent writes a verbal self-critique → stored in memory → guides future attempts.
              </div>
              {['Episode runs, agent records outcome','Evaluator grades the outcome (pass/fail)','LLM generates verbal reflection: "I failed because…"','Reflection stored in episodic memory','Next episode: reflection injected as context','Agent avoids same mistake via self-feedback'].map((s,i)=>(
                <div key={i} style={{fontSize:'9px',color:'var(--t2)',padding:'4px 8px',borderLeft:`2px solid ${P[i%P.length]}`,marginBottom:'3px'}}>{s}</div>
              ))}
            </Panel>
            <Panel title="SELF-CONSISTENCY" accent="#fbbf24">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                Sample N reasoning paths → majority vote on final answer. More robust than single CoT pass. Best with temperature &gt; 0.
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={[{n:1,acc:72},{n:3,acc:80},{n:5,acc:84},{n:10,acc:88},{n:20,acc:91},{n:40,acc:92}]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="n" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'N paths',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[65,95]}/>
                  <Tooltip/>
                  <Bar dataKey="acc" fill="#fbbf24" radius={[2,2,0,0]} name="Accuracy %"/>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
